import type { Tool } from '@toolproof-core/genesis/_contract/_index';

import {
    extractResourceTypeHandleFromRole,
    getIntrinsicInputRoleNames,
} from '@toolproof-core/lib/_pure/workflow-extraction';
import type {
    MutableToolStep,
    MutableToolStepRoleAddress,
    MutableWorkflowState,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import { withMergedEntityItemParametricState } from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type { CollectionKey, ConstellationKey } from '@toolproof-core/visualization/_contract/key';
import type {
    ConnectorItemCollectionDerivation,
    ConnectorItemDerivation,
    EntityItemCollectionDerivation,
    EntityItemDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    getResourceTypeRingRadius,
    getToolRingOuterRadius,
    getToolStepRingGap,
    resolveToolStepLayoutConfig,
    resolveWorkflowStateEntriesConfig,
} from '@/workflow/_pure/config/derivationConfig';
import { isWorkflowStateEntryConnectorsEnabled } from '@/workflow/_pure/config/derivationConfig';
import {
    buildToolStepItemCollectionDerivation,
    buildWorkflowStateEntryConnectorItemCollectionDerivations,
    buildWorkflowStateEntryItemCollectionDerivations,
} from '@/workflow/_pure/derivation/collections';
import {
    appendConnectorItemDerivationToCollectionGroup,
    deriveWorkflowStateEntryConnectorEndpointPair,
    resolveSourceRoleDirectionAndValue,
} from '@/workflow/_pure/derivation/connectors';
import {
    buildToolStepConstellationDerivation,
    buildWorkflowStateEntryConnectorConstellationDerivations,
    buildWorkflowStateEntryConstellationDerivations,
} from '@/workflow/_pure/derivation/constellations';
import {
    buildItemOffsetsForSpoke,
    deriveMaximumAdjacentWorkflowStateEntryArity,
    deriveRadialLayoutMetrics,
    validateRadialLayoutConfig,
} from '@/workflow/_pure/derivation/geometry';
import {
    toToolStepEntityItemDerivation,
    toWorkflowStateEntryConnectorItemDerivation,
    toWorkflowStateEntryItemDerivation,
} from '@/workflow/_pure/derivation/itemFactories';
import { deriveResourceTypeAngularInfo } from '@/workflow/_pure/derivation/resourceTypeAngularPlacement';
import { deriveUniformToolStepRingLayout } from '@/workflow/_pure/derivation/toolStepRingLayout';
import { resolveWorkflowStateEntryResolution } from '@/workflow/_pure/derivation/workflowStateEntryResolution';
import { parseWorkflowStateEntryCollectionKey, type WorkflowKeyScope } from '@/workflow/_pure/keyCodec';
import {
    collectDraftToolSteps,
    collectMutableWorkflowToolSteps,
} from '@/workflow-builder/_pure/model/machineWorkflow';
import type { Config } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { WorkflowStateEntryItemKey, WorkflowStateEntryResourceTypeHandle } from '@/workflow-builder/_contract/derivation';
import type { RuntimeState } from '@/workflow-builder/_contract/runtime';
import type { ProjectionRuntimeState } from '@/workflow-builder/_pure/derivation/projectionRuntimeState';
import { deriveCaseMembership } from '@/workflow-builder/_pure/derivation/caseMembership';

export function isManagedInputRole(args: {
    direction: 'input' | 'output';
    roleName: string;
    toolStep: MutableToolStep | undefined;
    tool: Tool | undefined;
    isForDraftWhenStep: boolean;
}): boolean {
    if (args.direction !== 'input' || args.toolStep === undefined) return false;

    const isIntrinsicInput = args.tool !== undefined
        && getIntrinsicInputRoleNames(args.tool).some((roleName) => String(roleName) === args.roleName);
    const isEngineManagedDynamicInput = args.isForDraftWhenStep
        && (args.toolStep.whenInputRoleBindingSpec?.dynamicInputRoleNames ?? [])
            .some((roleName) => String(roleName) === args.roleName);

    return isIntrinsicInput || isEngineManagedDynamicInput;
}

export function deriveCommittedToolStepItemCollectionDerivation(args: {
    itemCollectionKey: CollectionKey<'entity'>;
    runtimeState: ProjectionRuntimeState;
    config: Config;
}): EntityItemCollectionDerivation | undefined {
    const { committedToolSteps } = args.runtimeState;
    if (committedToolSteps.length === 0) return undefined;

    const toolStepLayoutConfig = resolveToolStepLayoutConfig(args.config);
    validateRadialLayoutConfig({
        toolRingOuterRadius: getToolRingOuterRadius(args.config),
        resourceTypeRingRadiusMultiplier: args.config.geometryScale.multipliers.resourceTypes.ringRadiusFromRoot,
        toolStepRingGapMultiplier: args.config.geometryScale.multipliers.toolSteps.ringGapFromResourceTypeRing,
    });
    const itemDerivations = committedToolSteps.map((toolStep) => toToolStepEntityItemDerivation({
        toolStep,
        config: toolStepLayoutConfig,
    }));

    return buildToolStepItemCollectionDerivation({
        itemCollectionKey: args.itemCollectionKey,
        itemDerivations,
    });
}

export function deriveToolStepRingLayout(args: {
    cosmosData: CosmosDataModel;
    projectedToolSteps: readonly MutableToolStep[];
    config: Config;
}) {
    const toolStepLayoutConfig = resolveToolStepLayoutConfig(args.config);
    const maximumArity = deriveMaximumAdjacentWorkflowStateEntryArity({
        orderedToolSteps: [...args.projectedToolSteps],
        toolMap: args.cosmosData.toolMap,
    });
    const radialLayout = deriveRadialLayoutMetrics({
        resourceTypeRingRadius: getResourceTypeRingRadius(args.config),
        toolStepRingGap: getToolStepRingGap(args.config),
        toolStepCount: args.projectedToolSteps.length,
        maxArityBetweenToolSteps: maximumArity,
    });
    const startRadius = radialLayout.toolStepRingRadii[0] ?? radialLayout.resourceTypeRingRadius;
    const resourceTypeSphereRadius =
        args.config.domainPresentation.entities.resourceTypes.item?.shape?.radius ?? 0;
    const ringLayout = deriveUniformToolStepRingLayout({
        stepCount: args.projectedToolSteps.length,
        startRadius,
        minimumRingGap: radialLayout.toolStepRingGap,
        minimumInnerBoundaryRadius:
            getResourceTypeRingRadius(args.config) + resourceTypeSphereRadius,
        envelopeByStepIndex: args.projectedToolSteps.map(() => ({ inward: 0, outward: 0 })),
    });
    return {
        ...ringLayout,
        workflowStateEntryGap: radialLayout.workflowStateEntryGap,
    };
}

export function deriveCommittedToolStepConstellationDerivation(args: {
    constellationKey: ConstellationKey<'entity'>;
    toolStepCollection: ItemCollection<'entity'>;
    cosmosData: CosmosDataModel;
    projectedToolSteps: readonly MutableToolStep[];
    config: Config;
    runtimeState: RuntimeState;
}): ReturnType<typeof buildToolStepConstellationDerivation> {
    const toolStepLayoutConfig = resolveToolStepLayoutConfig(args.config);
    const { startRadius, ringGap, ringRadii } = deriveToolStepRingLayout({
        cosmosData: args.cosmosData,
        projectedToolSteps: args.projectedToolSteps,
        config: args.config,
    });
    const tubeRadii = args.toolStepCollection.items.map(() => toolStepLayoutConfig.tubeRadius);

    return buildToolStepConstellationDerivation({
        constellationKey: args.constellationKey,
        toolStepCollection: args.toolStepCollection,
        firstToolStepItemKey: args.toolStepCollection.items[0].itemKey,
        startRadius,
        ringGap,
        ringRadii,
        tubeRadii,
        toolStepLayoutConfig,
        caseMemberships: deriveCaseMembership({
            runtimeState: args.runtimeState, projectedToolSteps: args.projectedToolSteps,
        }),
    });
}

export function deriveCommittedWorkflowStateEntryCollections(args: {
    cosmosData: CosmosDataModel;
    config: Config;
    builderRuntimeState: RuntimeState;
    runtimeState: ProjectionRuntimeState;
    itemCollections: ItemCollection[];
    toolStepCollection: ItemCollection<'entity'>;
    includeAllInputRolesByStepKeySet?: ReadonlySet<string>;
    workflowKeyScope: WorkflowKeyScope;
}): {
    itemCollectionDerivations: EntityItemCollectionDerivation[];
    constellationDerivations: ReturnType<typeof buildWorkflowStateEntryConstellationDerivations>;
} {
    const workflowStateEntriesConfig = resolveWorkflowStateEntriesConfig(args.config);
    if (!workflowStateEntriesConfig.enabled) {
        return { itemCollectionDerivations: [], constellationDerivations: [] };
    }

    const resourceTypeInfo = deriveResourceTypeAngularInfo({
        itemCollections: args.itemCollections,
        config: args.config,
    });
    if (resourceTypeInfo.resourceTypeCount === 0) {
        return { itemCollectionDerivations: [], constellationDerivations: [] };
    }

    const toolStepLayoutConfig = resolveToolStepLayoutConfig(args.config);
    const workflowState = args.runtimeState.committedWorkflowStateEntries as unknown as MutableWorkflowState;
    const workflowStateEntriesDeclaration = args.config.domainPresentation.entities.workflowStateEntries;
    const toolStepByKey = new Map<string, MutableToolStep>(
        collectMutableWorkflowToolSteps(args.builderRuntimeState.mutableWorkflow).map((toolStep) => [toolStep.stepKey, toolStep]),
    );
    if (args.builderRuntimeState.draft !== null) {
        for (const draftToolStep of collectDraftToolSteps(args.builderRuntimeState.draft)) {
            toolStepByKey.set(draftToolStep.stepKey, draftToolStep);
        }
    }
    const forDraftWhenStepKey = args.builderRuntimeState.draft?.draftKind === 'for'
        ? (args.builderRuntimeState.draft.caseDraft.when?.stepKey ?? null)
        : null;
    const projectedToolSteps = [...toolStepByKey.values()];
    const ringLayout = deriveToolStepRingLayout({
        cosmosData: args.cosmosData,
        projectedToolSteps,
        config: args.config,
    });

    const enrichWorkflowStateEntryItemDerivation = (itemDerivation: EntityItemDerivation, entryArgs: {
        stepKey: string;
        direction: 'input' | 'output';
        roleName: string;
    }): EntityItemDerivation => {
        const resolution = resolveWorkflowStateEntryResolution({
            direction: entryArgs.direction,
            stepKey: entryArgs.stepKey,
            roleName: entryArgs.roleName,
            workflowState,
            isInputFilled: ({ resolved }) => {
                if (resolved.status === 'externalInputPotential') return false;
                if (resolved.status === 'unresolved' && resolved.reason === 'not-found') {
                    return resolved.path.length > 1;
                }
                return true;
            },
            isOutputFilled: ({ stepKey }) => {
                if (stepKey === args.runtimeState.activeDraftStepKey) {
                    return args.runtimeState.activeDraftHasAllRequiredBindings;
                }

                return true;
            },
        });
        const baseOpacity = (() => {
            const configured = entryArgs.direction === 'input'
                ? workflowStateEntriesDeclaration.item.input.visualStyle?.opacity
                : workflowStateEntriesDeclaration.item.output.visualStyle?.opacity;
            return typeof configured === 'number' ? configured : 1;
        })();
        const toolStep = toolStepByKey.get(entryArgs.stepKey);
        const isManagedInput = isManagedInputRole({
            direction: entryArgs.direction,
            roleName: entryArgs.roleName,
            toolStep,
            tool: toolStep === undefined
                ? undefined
                : args.builderRuntimeState.toolMap.get(toolStep.toolHandle),
            isForDraftWhenStep: forDraftWhenStepKey === entryArgs.stepKey,
        });
        const previousState = itemDerivation.parametricState;

        return withMergedEntityItemParametricState(itemDerivation, {
            shape: previousState?.shape?.shapeKind === 'panel'
                ? {
                    ...previousState.shape,
                    appearanceMode: resolution.isFilled ? 'filled' : 'outlined',
                }
                : previousState?.shape,
            visualStyle: {
                opacity: isManagedInput ? baseOpacity * 0.4 : baseOpacity,
            },
            label: resolution.projectionText === null
                ? {}
                : {
                    onItem: {
                        ...(previousState?.label?.onItem ?? {}),
                        ...(workflowStateEntriesDeclaration.item.label?.onItem ?? {}),
                        text: resolution.projectionText,
                    },
                },
        });
    };

    const seeds: Parameters<typeof buildWorkflowStateEntryItemCollectionDerivations>[0]['seeds'] = [];

    const appendSeedForDirection = (seedArgs: {
        stepIndex: number;
        stepKey: string;
        stepItemKey: ItemCollection<'entity'>['items'][number]['itemKey'];
        direction: 'input' | 'output';
        roleNamesByResourceType: ReadonlyMap<WorkflowStateEntryResourceTypeHandle, string[]>;
    }) => {
        for (const [resourceTypeHandle, roleNames] of seedArgs.roleNamesByResourceType.entries()) {
            const angle = resourceTypeInfo.angleByHandle.get(resourceTypeHandle);
            if (angle == null) continue;
            const orderedRoleNames = seedArgs.direction === 'input'
                ? [...roleNames].reverse()
                : [...roleNames];
            const itemDerivations = orderedRoleNames.map((roleName) => enrichWorkflowStateEntryItemDerivation(
                toWorkflowStateEntryItemDerivation({
                    stepKey: seedArgs.stepKey,
                    direction: seedArgs.direction,
                    resourceTypeHandle,
                    roleName,
                    config: args.config,
                }),
                {
                    stepKey: seedArgs.stepKey,
                    direction: seedArgs.direction,
                    roleName,
                },
            ));
            if (itemDerivations.length === 0) continue;

            const itemOffsets = buildItemOffsetsForSpoke({
                itemCount: itemDerivations.length,
                workflowStateEntryGap: ringLayout.workflowStateEntryGap,
            });

            seeds.push({
                stepIndex: seedArgs.stepIndex,
                stepKey: seedArgs.stepKey,
                stepItemKey: seedArgs.stepItemKey,
                direction: seedArgs.direction,
                resourceTypeHandle,
                angle,
                stepRadius: ringLayout.ringRadii[seedArgs.stepIndex]
                    ?? (ringLayout.startRadius + (seedArgs.stepIndex * ringLayout.ringGap)),
                radialOffset: ringLayout.workflowStateEntryGap,
                itemDerivations,
                itemOffsets,
            });
        }
    };

    args.runtimeState.committedToolSteps.forEach((toolStep: MutableToolStep, stepIndex) => {
        const tool = args.cosmosData.toolMap.get(toolStep.toolHandle);
        if (!tool) return;

        const committedStepEntries = args.runtimeState.committedWorkflowStateEntries[toolStep.stepKey] ?? {};
        const boundRoleNames = Object.keys(committedStepEntries);
        const includeAllInputRoles = args.includeAllInputRolesByStepKeySet?.has(toolStep.stepKey) ?? false;
        const inputRoleNames = includeAllInputRoles
            ? Array.from(new Set([...toolStep.roleBindingSpec.inputBindings, ...boundRoleNames]))
            : boundRoleNames;
        if (inputRoleNames.length === 0 && !includeAllInputRoles) return;

        const stepItem = args.toolStepCollection.items[stepIndex];
        if (!stepItem) return;

        const groupedInputByResourceType = new Map<WorkflowStateEntryResourceTypeHandle, string[]>();
        for (const roleName of inputRoleNames) {
            const role = tool.roleSpec.inputRoleByName[roleName];
            if (!role) continue;
            const resourceTypeHandle = extractResourceTypeHandleFromRole(role);
            if (!resourceTypeHandle) continue;
            const typedResourceTypeHandle = resourceTypeHandle as WorkflowStateEntryResourceTypeHandle;

            const current = groupedInputByResourceType.get(typedResourceTypeHandle) ?? [];
            current.push(roleName);
            groupedInputByResourceType.set(typedResourceTypeHandle, current);
        }

        const groupedOutputByResourceType = new Map<WorkflowStateEntryResourceTypeHandle, string[]>();
        for (const [roleName, role] of Object.entries(tool.roleSpec.outputRoleByName)) {
            const resourceTypeHandle = extractResourceTypeHandleFromRole(role);
            if (!resourceTypeHandle) continue;
            const typedResourceTypeHandle = resourceTypeHandle as WorkflowStateEntryResourceTypeHandle;

            const current = groupedOutputByResourceType.get(typedResourceTypeHandle) ?? [];
            current.push(roleName);
            groupedOutputByResourceType.set(typedResourceTypeHandle, current);
        }

        appendSeedForDirection({
            stepIndex,
            stepKey: toolStep.stepKey,
            stepItemKey: stepItem.itemKey,
            direction: 'output',
            roleNamesByResourceType: groupedOutputByResourceType,
        });

        appendSeedForDirection({
            stepIndex,
            stepKey: toolStep.stepKey,
            stepItemKey: stepItem.itemKey,
            direction: 'input',
            roleNamesByResourceType: groupedInputByResourceType,
        });
    });

    const itemCollectionDerivations = buildWorkflowStateEntryItemCollectionDerivations({
        seeds,
        scope: args.workflowKeyScope,
    });
    const constellationDerivations = buildWorkflowStateEntryConstellationDerivations({
        seeds,
        workflowStateEntriesConfig,
        scope: args.workflowKeyScope,
    });

    return { itemCollectionDerivations, constellationDerivations };
}

export function deriveCommittedWorkflowStateEntryConnectorCollections(args: {
    cosmosData: CosmosDataModel;
    config: Config;
    runtimeState: ProjectionRuntimeState;
    itemCollections: ItemCollection[];
    workflowKeyScope: WorkflowKeyScope;
}): {
    itemCollectionDerivations: ConnectorItemCollectionDerivation[];
    constellationDerivations: ReturnType<typeof buildWorkflowStateEntryConnectorConstellationDerivations>;
} {
    if (!isWorkflowStateEntryConnectorsEnabled(args.config)) {
        return { itemCollectionDerivations: [], constellationDerivations: [] };
    }

    const workflowStateEntryItemKeySet = new Set<WorkflowStateEntryItemKey>();
    for (const collection of args.itemCollections) {
        if (collection.itemCollectionKind !== 'entity') continue;
        if (!parseWorkflowStateEntryCollectionKey(collection.itemCollectionKey)) continue;
        for (const item of collection.items) {
            workflowStateEntryItemKeySet.add(item.itemKey);
        }
    }

    const stepIndexByStepKey = new Map<string, number>();
    const toolByStepKey = new Map<string, Tool>();
    args.runtimeState.committedToolSteps.forEach((toolStep, stepIndex) => {
        stepIndexByStepKey.set(toolStep.stepKey, stepIndex);
        const tool = args.cosmosData.toolMap.get(toolStep.toolHandle);
        if (tool) {
            toolByStepKey.set(toolStep.stepKey, tool);
        }
    });

    const groupedItemDerivationsByCollectionKey = new Map<ItemCollection<'connector'>['itemCollectionKey'], ConnectorItemDerivation[]>();

    for (const targetToolStep of args.runtimeState.committedToolSteps) {
        const targetStepKey = targetToolStep.stepKey;
        const targetTool = toolByStepKey.get(targetStepKey);
        if (!targetTool) continue;

        const targetStepIndex = stepIndexByStepKey.get(targetStepKey);
        if (targetStepIndex == null) continue;

        const targetInputRoleByName = targetTool.roleSpec.inputRoleByName as Record<string, unknown>;
        const targetInputEntries = args.runtimeState.committedWorkflowStateEntries[targetStepKey] ?? {};

        for (const [targetRoleName, workflowStateEntry] of Object.entries(targetInputEntries)) {
            if (!workflowStateEntry || workflowStateEntry.workflowStateEntryKind !== 'internalInputPotential') {
                continue;
            }
            const typedSourceAddress = workflowStateEntry.toolStepRoleAddress as MutableToolStepRoleAddress;

            const sourceStepIndex = stepIndexByStepKey.get(typedSourceAddress.stepKey);
            if (sourceStepIndex == null || sourceStepIndex >= targetStepIndex) continue;

            const targetRole = targetInputRoleByName[targetRoleName];
            if (!targetRole) continue;
            const targetResourceTypeHandle = extractResourceTypeHandleFromRole(
                targetRole as Parameters<typeof extractResourceTypeHandleFromRole>[0]
            );
            if (!targetResourceTypeHandle) continue;
            const typedTargetResourceTypeHandle = targetResourceTypeHandle as WorkflowStateEntryResourceTypeHandle;

            const sourceTool = toolByStepKey.get(typedSourceAddress.stepKey);
            if (!sourceTool) continue;

            const sourceRole = resolveSourceRoleDirectionAndValue({
                tool: sourceTool,
                roleName: typedSourceAddress.roleName,
            });
            if (!sourceRole) continue;

            const sourceRoleValue = sourceRole.role;
            if (!sourceRoleValue) continue;
            const sourceResourceTypeHandle = extractResourceTypeHandleFromRole(
                sourceRoleValue as Parameters<typeof extractResourceTypeHandleFromRole>[0]
            );
            if (!sourceResourceTypeHandle) continue;
            const typedSourceResourceTypeHandle = sourceResourceTypeHandle as WorkflowStateEntryResourceTypeHandle;

            const endpointPair = deriveWorkflowStateEntryConnectorEndpointPair({
                ...args.workflowKeyScope,
                targetStepKey,
                targetRoleName,
                targetResourceTypeHandle: typedTargetResourceTypeHandle,
                sourceStepKey: typedSourceAddress.stepKey,
                sourceRoleName: typedSourceAddress.roleName,
                sourceDirection: sourceRole.direction,
                sourceResourceTypeHandle: typedSourceResourceTypeHandle,
                workflowStateEntryItemKeySet,
            });
            if (!endpointPair) continue;

            appendConnectorItemDerivationToCollectionGroup({
                groupedItemDerivationsByCollectionKey,
                collectionKey: endpointPair.collectionKey,
                connectorItemDerivation: toWorkflowStateEntryConnectorItemDerivation({
                    targetStepKey,
                    targetRoleName,
                    sourceStepKey: typedSourceAddress.stepKey,
                    sourceRoleName: typedSourceAddress.roleName,
                    sourceDirection: sourceRole.direction,
                    resourceTypeHandle: typedTargetResourceTypeHandle,
                    targetItemKey: endpointPair.targetItemKey,
                    sourceItemKey: endpointPair.sourceItemKey,
                    config: args.config,
                }),
            });
        }
    }

    const itemCollectionDerivations = buildWorkflowStateEntryConnectorItemCollectionDerivations({
        itemDerivationsByCollectionKey: groupedItemDerivationsByCollectionKey,
    });

    const constellationDerivations = buildWorkflowStateEntryConnectorConstellationDerivations({
        itemCollections: [
            ...args.itemCollections,
            ...itemCollectionDerivations.map((itemCollectionDerivation) => ({
                itemCollectionKey: itemCollectionDerivation.itemCollectionKey,
                itemCollectionKind: 'connector' as const,
                items: itemCollectionDerivation.itemDerivations.map((itemDerivation) => itemDerivation.item),
            })),
        ],
    });

    return { itemCollectionDerivations, constellationDerivations };
}
